// Ticket statuses, the admin steps behind the buttons, and display helpers.
// The buttons are driven by the `allowed_steps` the server returns for the
// ticket and the caller's role, never by the status alone.

export const STATUSES = [
  'NEW',
  'PIC_NOTIFIED',
  'IN_PROGRESS',
  'AWAITING_SLOT',
  'SCHEDULED',
  'REOPENED',
  'NEEDS_ADMIN',
  'RESOLVED',
  'CLOSED',
];

export const OPEN_STATUSES = STATUSES.slice(0, 7);

export const isOpenStatus = status => OPEN_STATUSES.includes(status);

export const STATUS_STYLE = {
  NEW: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  PIC_NOTIFIED: 'bg-woot-100 text-woot-700 dark:bg-woot-900 dark:text-woot-300',
  IN_PROGRESS: 'bg-woot-100 text-woot-700 dark:bg-woot-900 dark:text-woot-300',
  AWAITING_SLOT:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300',
  SCHEDULED:
    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  REOPENED:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300',
  NEEDS_ADMIN: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
  RESOLVED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  CLOSED: 'bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300',
};

export const SLA_STYLE = {
  on_track: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  at_risk:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300',
  breached: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
};

export const SOURCES = ['chatbot', 'walkin', 'phone', 'other'];
// A manual ticket is never from the chatbot.
export const MANUAL_SOURCES = ['walkin', 'phone', 'other'];
export const SLA_STATES = ['on_track', 'at_risk', 'breached'];
export const LOCATION_TYPES = ['in_room', 'common_area', 'unsure'];
export const SEVERITIES = ['low', 'medium', 'high', 'urgent'];

// The list tabs: "Open" is every open status, the rest one status each.
export const TABS = [
  { key: 'open', statuses: OPEN_STATUSES },
  ...STATUSES.map(status => ({ key: status, statuses: [status] })),
];

export const statusQuery = tabKey => {
  const tab = TABS.find(t => t.key === tabKey) || TABS[0];
  return tab.statuses.join(',');
};

// What each step sends. `needs` names the input the step asks for first:
//   reason   a required reason (kept in the ticket history)
//   note     an optional note to the tenant (resolve only)
//   eta, tenant_note   the notes form (PUT /notes)
//   assign   the member picker (PUT /assignee)
// `to` is the status or step name for PATCH /status.
export const STEP_DEFS = {
  acknowledge: { to: 'IN_PROGRESS' },
  needs_visit: { to: 'AWAITING_SLOT' },
  resolved: { to: 'RESOLVED', needs: 'note' },
  close: { to: 'CLOSED' },
  cancel: { to: 'CLOSED', needs: 'reason', danger: true },
  reopen: { to: 'REOPENED', needs: 'reason' },
  hand_back: { to: 'NEEDS_ADMIN', needs: 'reason' },
  eta: { needs: 'eta' },
  tenant_note: { needs: 'tenant_note' },
  assign: { needs: 'assign' },
};

// no_show happens on a visit, so it is not a ticket-level button.
export const stepsToShow = allowedSteps =>
  (allowedSteps || []).filter(step => STEP_DEFS[step]);

// The PATCH /status body for a step. Reason and note are trimmed; an empty
// note is left out.
export function statusBody(step, { reason = '', note = '' } = {}) {
  const def = STEP_DEFS[step];
  const body = { to: def.to };
  if (reason.trim()) body.reason = reason.trim();
  if (def.needs === 'note' && note.trim()) body.note = note.trim();
  return body;
}

// Reasons are required for cancel, reopen and hand back (422 reason_required).
export const stepNeedsReason = step => STEP_DEFS[step]?.needs === 'reason';
export const REASON_STEPS = Object.keys(STEP_DEFS).filter(stepNeedsReason);

// The status menu in the list, in the order of the ticket's life. Only steps
// that change the status (or assign) belong: eta and tenant_note live in the
// notes panel, no_show on a visit.
const STATUS_MENU_ORDER = [
  'acknowledge',
  'needs_visit',
  'assign',
  'resolved',
  'close',
  'reopen',
  'hand_back',
  'cancel',
];

// Label key under CO_LIVING.TICKETS: the status a step leads to, except where
// that would be ambiguous (cancel and close both end in Closed), the step is
// not a plain status change (assign picks a person), or the status name reads
// as a state rather than an action (hand back).
const STATUS_MENU_LABEL = {
  assign: 'STEPS.ASSIGN',
  cancel: 'STEPS.CANCEL',
  // "Needs admin" reads like a state; the button on the ticket says this.
  hand_back: 'STEPS.HAND_BACK',
};

// What the status menu offers for a ticket, from the server's `allowed_steps`
// (never from a table here: the server decides, and agents get none).
export const statusMenuEntries = allowedSteps =>
  STATUS_MENU_ORDER.filter(step => (allowedSteps || []).includes(step)).map(
    step => ({
      step,
      label: STATUS_MENU_LABEL[step] || `STATUS.${STEP_DEFS[step].to}`,
      danger: !!STEP_DEFS[step].danger,
    })
  );

// "Set status": the server lists every other status an admin may pick now
// (`allowed_statuses`) and why the rest cannot be (`blocked_statuses`, a map of
// status to code). The FE keeps no rules of its own.
export const setStatusChoices = ticket => {
  const blocked = (ticket && ticket.blocked_statuses) || {};
  return {
    allowed: (ticket && ticket.allowed_statuses) || [],
    blocked: Object.keys(blocked).map(status => ({
      status,
      code: blocked[status],
    })),
  };
};

// A ticket can be reassigned while it is open, except in Needs admin where
// "Assign" does the same job. Agents (no allowed steps) never see it.
export const canReassign = ticket =>
  !!ticket &&
  (ticket.allowed_steps || []).length > 0 &&
  isOpenStatus(ticket.status) &&
  ticket.status !== 'NEEDS_ADMIN';

// `datetime-local` gives "2026-10-30T15:00"; the API reads a time without an
// offset in the account timezone, so it is sent as it is. An empty input
// clears the ETA.
export const etaPayload = value => value || null;

// A stored UTC time ("2026-10-30T07:00:00Z") as the "YYYY-MM-DDTHH:mm" a
// datetime-local input wants, in the account timezone. That matches how the
// API reads a new ETA: a time without an offset is in the account timezone.
export function isoToInputInZone(iso, timezone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: timezone || undefined,
    })
      .formatToParts(date)
      .forEach(part => {
        parts[part.type] = part.value;
      });
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch (e) {
    return '';
  }
}

// "2026-10-28T07:11:24Z" shown in the account timezone, e.g. "28 Oct 2026,
// 15:11". An invalid zone or date falls back to the raw text.
export function formatTimestamp(iso, timezone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      // h23, not hour12:false: that prints midnight as "24:05" in Chromium.
      hourCycle: 'h23',
      timeZone: timezone || undefined,
    }).format(date);
  } catch (e) {
    return String(iso);
  }
}

// A machine code from the server such as "no_routing_rule" or "needs_visit".
// Free text a person typed has spaces or capitals and is left alone.
const CODE_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;

// Readable fallback for a code with no translation: "No routing rule".
export const humanizeCode = code =>
  CODE_PATTERN.test(code)
    ? code.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
    : code;

// The first user-visible line for a history row: its free text, else the
// reason or step, run through `label` (a translator for server codes).
// Steps whose `text` is only a detail (which visit), so the label leads.
const LABEL_THEN_TEXT_STEPS = ['visit_expired'];

export const historyText = (row, label = code => code) => {
  if (LABEL_THEN_TEXT_STEPS.includes(row.step) && row.text) {
    return `${label(row.step)} (${row.text})`;
  }
  return row.text || label(row.reason || row.step);
};

// Where a ticket is, split for the list columns: the building (condo), the
// unit, and the place inside it. `place` is the room, 'common_area' or
// 'whole_unit' (a unit ticket with no room, e.g. a studio); the page turns the
// last two into words. A common-area ticket has no unit.
export function ticketLocation(ticket) {
  const commonArea = !!ticket.common_area;
  const unit = commonArea ? '' : ticket.unit || '';
  let place = '';
  if (commonArea) place = 'common_area';
  else if (ticket.room) place = ticket.room;
  else if (unit) place = 'whole_unit';
  return { building: ticket.condo || '', unit, place, commonArea };
}

// The messages panel. One event can make several rows (a WhatsApp alert and
// its bell twin, a failure and the alerts it raised), so rows are grouped by
// `event_id`, in the order the server sent them.
export function messageGroups(messages) {
  const groups = [];
  const byEvent = {};
  (messages || []).forEach(message => {
    const key =
      message.event_id == null ? `row-${message.id}` : message.event_id;
    if (!byEvent[key]) {
      byEvent[key] = { eventId: key, rows: [] };
      groups.push(byEvent[key]);
    }
    byEvent[key].rows.push(message);
  });
  return groups;
}

// Which words explain a message's `error`, by the code it starts with. The
// raw error stays available for a tooltip.
const MESSAGE_ERROR_PREFIXES = [
  ['template_not_approved', 'TEMPLATE_NOT_APPROVED'],
  ['no_outbound_inbox', 'NO_OUTBOUND_INBOX'],
  ['no_agent_bot', 'NO_AGENT_BOT'],
  ['interrupted', 'UNKNOWN_OUTCOME'],
  ['read_timeout', 'UNKNOWN_OUTCOME'],
  ['http ', 'CHATWOOT_REFUSED'],
  ['meta:', 'NOT_DELIVERED'],
  ['lookup:', 'RETRYING'],
  ['connect:', 'RETRYING'],
  ['no_user_token', 'ADMIN_NOTIFICATION_FAILED'],
  ['portal:', 'ADMIN_NOTIFICATION_FAILED'],
  ['error:', 'UNEXPECTED'],
];

export const messageErrorKey = error => {
  if (!error) return '';
  const hit = MESSAGE_ERROR_PREFIXES.find(([prefix]) =>
    String(error).startsWith(prefix)
  );
  return hit ? hit[1] : '';
};

export const MESSAGE_STATUS_STYLE = {
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300',
  sending: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  sent: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  delivered:
    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  read: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
};

export const MAX_OFFER_SLOTS = 3;

// Picking slots for a visit. `selected` is a list of { date, start, label };
// the same slot clicked again is removed, and a full list refuses a new one
// (returned unchanged). `max` is 3 for an offer, 1 for a booking.
export function toggleSlot(selected, slot, max) {
  const same = s => s.date === slot.date && s.start === slot.start;
  if (selected.some(same)) return selected.filter(s => !same(s));
  if (max === 1) return [slot];
  return selected.length >= max ? selected : [...selected, slot];
}

// The POST /visits body: `book` needs exactly one slot, `offer` one to three.
export function visitBody({ mode, purpose, minutes, slots }) {
  return {
    mode,
    purpose,
    minutes: Number(minutes),
    slots: slots.map(({ date, start }) => ({ date, start })),
  };
}

// Visit buttons by status (the handoff's visit table).
export const visitButtons = visit => {
  if (visit.status === 'Offered') return ['confirm', 'withdraw'];
  if (visit.status === 'Confirmed') {
    return ['reschedule', 'cancel', 'complete', 'no_show'];
  }
  return [];
};

export const PURPOSES = [
  'Diagnose the issue',
  'Repair',
  'Replace a part',
  'Follow-up check',
  'Other',
];
